/**
 * 표 제목 옆 "반영불가 N건" 칩과, 그걸 눌렀을 때 펼쳐지는 "반영되지 않은 값".
 *
 * 로그상 사용자는 이 칩을 눌러 이유를 찾으려 했지만 아무 반응이 없었다.
 * 칩을 버튼으로 바꿔, 사유별로 묶은 미반영 항목과 그 상세 사유를 펼친다.
 * 항목을 누르면 그 값이 있어야 할 워크북 셀로 데려간다 — 사용자가 고칠 자리는
 * 목록이 아니라 셀이다.
 *
 * 시안: Figma INVECTOR 2.0
 * - 칩(닫힘) `8877:795929` / 칩(열림) `8860:578600` / 패널 `8881:798007`
 * - 사유 탭 `9001:536622` / `9001:536710`
 */

import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  TriangleAlert,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { TONE_TEXT } from "@/components/ixd-table";
import { cn } from "@/lib/utils";
import { TextChip } from "@/toggle-ui-kit/chip";
import { IconButton } from "@/toggle-ui-kit/icon-button";
import { ViewSwitchGroup } from "@/toggle-ui-kit/view-switch-group";
import {
  countUnappliedCells,
  type PlanTableDiagnostics,
  type UnappliedGroup,
} from "./plan-table-diagnostics";

/** 데려온 셀에 테두리를 둘러두는 시간. 눈이 따라올 만큼만 */
const FOCUS_FLASH_MS = 1600;

/** 시안의 패널 폭(320px)과 목록 스크롤 높이(242px — 9001-536761 의 스크롤 트랙) */
const PANEL_WIDTH = "w-[320px]";
const PANEL_LIST_MAX_HEIGHT = "max-h-[242px]";

/*
 * 테두리의 수명은 패널 밖에서 센다.
 *
 * 항목을 고르면 패널은 그 자리에서 닫히고(unmount), 패널이 타이머를 들고
 * 있으면 정리 함수가 그 타이머를 같이 지워 테두리가 영영 남는다. 다음 항목을
 * 골라도 이전 테두리가 그대로라 "지금 어디로 왔는지"를 가리킨다는 뜻이 사라진다.
 *
 * ponytail: 모듈 전역 타이머 하나. 테두리는 화면에 늘 하나뿐이어야 하므로
 * 행마다 따로 셀 이유가 없다 — 표식을 새로 달기 전에 먼저 전부 지운다.
 */
let flashTimer: number | undefined;

const clearNoticeFocus = () => {
  window.clearTimeout(flashTimer);
  for (const marked of document.querySelectorAll("[data-notice-focused]")) {
    marked.removeAttribute("data-notice-focused");
  }
};

/**
 * 그 항목이 들어갔어야 할 셀로 데려간다.
 *
 * 셀은 자기 사유의 키를 `data-notice-key`로 달고 있고, 키에는 표마다 다른
 * 접두사가 붙어 있어 같은 화면의 다른 표를 잘못 짚지 않는다.
 */
const focusNoticeCell = (key: string): HTMLElement | null => {
  const cell = document.querySelector<HTMLElement>(
    // jsdom 엔 CSS.escape 가 없다 — features/dsd-review/cell-selector.ts 와 같은 처리
    `[data-notice-key="${globalThis.CSS?.escape?.(key) ?? key.replace(/["\\]/g, "\\$&")}"]`,
  );
  if (!cell) return null;
  clearNoticeFocus();
  cell.scrollIntoView({ behavior: "smooth", block: "center" });
  /*
   * 테두리만 깜빡이고 마는 게 아니라 셀에 실제 포커스를 준다 — 패널이 닫힌
   * 뒤 키보드가 그 셀에서 이어지고, 화면 낭독기도 어디로 왔는지 읽는다.
   * 표의 td는 원래 포커스 대상이 아니라 tabIndex를 심어 준다(탭 순서 밖: -1).
   */
  cell.tabIndex = -1;
  cell.focus({ preventScroll: true });
  cell.setAttribute("data-notice-focused", "");
  flashTimer = window.setTimeout(clearNoticeFocus, FOCUS_FLASH_MS);
  return cell;
};

const UnappliedGroupSection = ({
  group,
  onSelect,
  listRef,
}: {
  group: UnappliedGroup;
  onSelect: (key: string) => void;
  listRef: React.RefObject<HTMLUListElement>;
}) => (
  <>
    {/* 묶음 제목과 건수는 탭이 말한다 — 여기서는 조치 안내만 남긴다. */}
    <p className="px-1 caption-2-regular text-label-alternative">
      {group.hint}
    </p>
    {/*
     * 시안(9001-536761)은 목록만 스크롤하고 탭·안내는 그 위에 고정한다.
     * 스크롤바도 시안의 Scroll Bar 대로 목록 오른쪽에만 선다.
     */}
    <ul
      ref={listRef}
      className={cn(
        "mt-4 flex flex-col gap-1 overflow-y-auto scrollbar-overlay",
        PANEL_LIST_MAX_HEIGHT,
      )}
    >
      {group.items.map((item) => (
        <li key={item.key}>
          <button
            type="button"
            onClick={() => onSelect(item.key)}
            // 카드 전체가 버튼이다 — chevron 만 누르는 것으로 보이지 않게
            // 커서와 hover 배경으로 "어디를 눌러도 된다"를 알린다.
            className="flex w-full cursor-pointer flex-col gap-2 rounded-[8px] bg-background-gray-alternative p-2 text-left hover:bg-background-gray-normal"
          >
            <span className="flex w-full items-center justify-between gap-2">
              <span className="flex min-w-0 flex-col">
                <span className="truncate caption-2-regular text-label-alternative">
                  {item.label}
                </span>
                <span className="truncate caption-1-semibold text-label-normal">
                  {item.value}
                </span>
              </span>
              <ChevronRight className="size-5 shrink-0 text-label-normal" />
            </span>
            {/*
             * 시안(9001-536635)은 구분선과 상세를 카드 **안**에 둔다.
             * 사유 안내를 되풀이할 뿐인 상세는 비어 있다 — 그 줄은 띄우지 않고
             * 구분선도 같이 접는다(합계열 미체크 탭의 48px 카드).
             */}
            {item.detail && (
              <>
                <span aria-hidden className="h-px w-full bg-line-normal" />
                <span
                  className={cn(
                    "w-full caption-2-regular",
                    TONE_TEXT[group.tone],
                  )}
                >
                  {item.detail}
                </span>
              </>
            )}
          </button>
        </li>
      ))}
    </ul>
  </>
);

const UnappliedValuesPanel = ({
  groups,
  onClose,
}: {
  groups: UnappliedGroup[];
  onClose: () => void;
}) => {
  /*
   * 사유 묶음을 세로로 쌓으면, 건수가 많은 묶음(IXD 표에 같은 계정 없음)이
   * 스크롤을 다 차지해 아래의 합계열 안내가 보이지 않는다. 탭으로 가른다.
   *
   * 패널은 닫힐 때 unmount 되므로 상태 초기화는 따로 하지 않는다.
   * 묶음은 건수 많은 순이라 첫 탭이 곧 가장 큰 묶음이다.
   */
  const [activeReason, setActiveReason] = useState(groups[0]?.reason);
  /*
   * 재조회로 그 사유가 사라지면 빈 패널이 된다 — 첫 묶음으로 물러난다.
   * `activeReason` 은 사라진 사유를 그대로 들고 있다. 의도한 쪽이다 — 다음
   * 재조회에서 그 사유가 돌아오면 사용자가 고른 탭으로 되돌아간다.
   */
  const activeGroup =
    groups.find(({ reason }) => reason === activeReason) ?? groups[0];

  /*
   * 탭을 바꿀 때 목록 스크롤을 맨 위로. 브라우저는 scrollTop 을 새 콘텐츠
   * 높이에 맞춰 줄이기만 하므로, 50건 묶음에서 내린 뒤 20건 묶음으로 가면
   * 새 묶음의 첫 항목이 아니라 목록 중간에서 시작한다.
   */
  const listRef = useRef<HTMLUListElement>(null);
  const handleReasonChange = useCallback((reason: UnappliedGroup["reason"]) => {
    setActiveReason(reason);
    if (listRef.current) listRef.current.scrollTop = 0;
  }, []);

  const handleSelect = useCallback(
    (key: string) => {
      /*
       * 서버가 셀 주소를 안 준 실패(전기 쪽에서 흔하다)는 데려갈 셀이 없다.
       * 그때 패널까지 닫으면 "눌렀는데 아무 일도 안 일어난" 꼴이 되므로,
       * 셀을 찾았을 때만 비켜준다.
       */
      if (focusNoticeCell(key)) onClose();
    },
    [onClose],
  );

  return (
    <div
      role="dialog"
      aria-label="반영되지 않은 값"
      className={cn(
        "absolute left-0 top-[calc(100%+4px)] z-50 flex flex-col overflow-hidden",
        "rounded-[8px] border border-line-normal bg-background-base shadow-xxlarge",
        PANEL_WIDTH,
      )}
    >
      <div className="flex items-center justify-between gap-2 px-3 pb-1 pt-2">
        <span className="caption-1-semibold text-label-normal">
          반영되지 않은 값
        </span>
        <IconButton aria-label="닫기" onClick={onClose} icon={<X />} />
      </div>
      <div aria-hidden className="h-px w-full bg-line-normal" />
      <div className="flex flex-col gap-2 px-3 pb-3 pt-2">
        {/* 묶음이 하나면 고를 것이 없다 — 선택지 없는 탭 줄은 자리만 먹는다. */}
        {groups.length > 1 && (
          /*
           * 시안 9001-536809: 296px(패널 폭 - 좌우 여백)을 두 칸이 반씩 나눠
           * 갖는 탭 줄. 갈래가 둘뿐이라 넘칠 일이 없어 가로 스크롤은 두지 않고,
           * 혹시 라벨이 길어지면 시안처럼 칸 안에서 잘린다(overflow-hidden).
           */
          <ViewSwitchGroup
            size="28px"
            className="w-full"
            value={activeGroup.reason}
            onChange={handleReasonChange}
            options={groups.map((group) => ({
              value: group.reason,
              label: `${group.title}(${group.items.length})`,
              /*
               * 공용 28px 변형은 상단 바(연결/별도) 기준이라 칸마다 70px
               * 최소폭과 13px 문구를 쓴다. 시안의 탭은 폭을 반씩 나눠 갖고
               * 문구가 12px(Caption 1/Medium)이라 여기서만 덮는다.
               */
              className: "caption-1-medium min-w-0 flex-1 overflow-hidden",
            }))}
          />
        )}
        <UnappliedGroupSection
          group={activeGroup}
          onSelect={handleSelect}
          listRef={listRef}
        />
      </div>
    </div>
  );
};

/**
 * 표 제목 옆 미반영 요약.
 *
 * 판정 status가 아니라 실제 미반영 건수로 뜬다 — status가 failed가 아닌데
 * failures는 있는 표에서 셀에는 표식이 붙고 배지만 사라지면, 사용자는 표식을
 * 일일이 세어보기 전엔 몇 건이 빠졌는지 알 수 없다.
 *
 * 세는 단위는 **셀 자리**다. 서버 실패는 (셀 × 기간)이라 같은 계정이 당기·전기
 * 두 건으로 오는데, 그대로 세면 화면의 표식 개수와 배지 숫자가 어긋난다
 * (실측: 11-2 유형자산 36건 vs 표식 18개).
 */
export const UnappliedValuesBadge = ({
  diagnostics,
  groups,
}: {
  diagnostics: PlanTableDiagnostics;
  /** 펼칠 목록. 셀 표시와 같은 재료로 만든 것을 그대로 받는다 */
  groups: UnappliedGroup[];
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) close();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open, close]);

  if (diagnostics.notAppliedCount === 0) return null;
  // 배지·패널·셀 표식이 같은 수를 말하도록 셀 자리로 센다.
  const cellCount = countUnappliedCells(groups);
  /*
   * 표 자체가 없거나 짚을 셀이 하나도 없으면 펼칠 것이 없다. 눌러도 빈 패널이
   * 뜨는 버튼은 지금의 "눌러도 아무 반응 없음"과 다를 바 없으므로 표기만 한다.
   */
  if (diagnostics.isTableMissing || groups.length === 0) {
    return (
      <span role="status" className="shrink-0">
        <TextChip
          size="medium"
          className="border border-transparent bg-red-100 text-red-500"
          leftIcon={<TriangleAlert className="size-3" />}
        >
          {/* 짚을 셀이 없는 표라 셀 수를 셀 수 없다 — 값 건수로 알린다. */}
          {diagnostics.isTableMissing
            ? "IXD에 표 없음"
            : `반영불가 ${diagnostics.notAppliedCount}건`}
        </TextChip>
      </span>
    );
  }

  return (
    <div className="relative shrink-0" ref={rootRef}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        // inline-flex 로 감싼다 — block 이면 baseline 여백이 붙어 칩이 4px 커진다.
        className="flex cursor-pointer rounded-[4px]"
      >
        <TextChip
          size="medium"
          // 열림 상태는 테두리로 표시한다(시안). 자리가 밀리지 않게 평소엔 투명.
          className={cn(
            "border bg-red-100 text-red-500",
            open ? "border-red-500" : "border-transparent",
          )}
          leftIcon={<TriangleAlert className="size-3" />}
          rightIcon={
            open ? (
              <ChevronUp className="size-2.5" />
            ) : (
              <ChevronDown className="size-2.5" />
            )
          }
        >
          반영불가 {cellCount}건
        </TextChip>
      </button>
      {open && <UnappliedValuesPanel groups={groups} onClose={close} />}
    </div>
  );
};
